// LastAiReportCard — Feb 2026.
//
// Показва "Последен AI доклад · <дата>" на dashboard-а с:
//   1. Teaser: първите ~2 изречения от AI report-a
//   2. Ключови метрики (BMR, TDEE, дневни калории, протеин)
//   3. Бутон "Виж пълния анализ" → modal с целия текст
//
// Данни идват от `GET /api/user/health-report/latest`
// (`db.users.profile.latestHealthReport` cache, fallback към `db.health_reports`).
// Ако user още не е генерирал доклад — картата не се рендира.

import React, { useEffect, useState } from "react";
import axios from "axios";
import { useAuth } from "@/App";
import { Sparkles, FileText, X, Flame, Target, Utensils, RefreshCw } from "lucide-react";
import { toast } from "sonner";

function extractTeaser(text, maxChars = 240) {
  if (!text || typeof text !== "string") return "";
  // Take first 2 sentences OR first ~240 chars, whichever comes first.
  const cleaned = text.replace(/\s+/g, " ").trim();
  const match = cleaned.match(/[^.!?]+[.!?]+/g);
  if (match && match.length >= 2) {
    const two = match.slice(0, 2).join(" ").trim();
    if (two.length <= maxChars) return two;
  }
  return cleaned.length > maxChars ? cleaned.slice(0, maxChars).trim() + "…" : cleaned;
}

export default function LastAiReportCard({ isDark, bg }) {
  const { API } = useAuth();
  const [reportDoc, setReportDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showFull, setShowFull] = useState(false);
  const [regenerating, setRegenerating] = useState(false);

  const regenerate = async () => {
    if (regenerating) return;
    setRegenerating(true);
    try {
      const { data } = await axios.post(`${API}/user/health-report/regenerate`, {
        language: bg ? "bg" : "en",
      });
      if (data?.report) {
        // Refetch the freshly-saved doc (which also carries updated generated_at)
        const { data: latest } = await axios.get(`${API}/user/health-report/latest`);
        setReportDoc(latest?.report || null);
        toast.success(bg ? "AI докладът е обновен" : "AI report refreshed");
      }
    } catch {
      toast.error(bg ? "Грешка при обновяване на доклада" : "Failed to refresh the report");
    } finally {
      setRegenerating(false);
    }
  };

  useEffect(() => {
    if (!API) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/user/health-report/latest`);
        if (!cancelled) setReportDoc(data?.report || null);
      } catch {
        if (!cancelled) setReportDoc(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    // Refetch when a new report is generated (dispatched by OnboardingWizard)
    const refresh = () => {
      axios.get(`${API}/user/health-report/latest`)
        .then(({ data }) => { if (!cancelled) setReportDoc(data?.report || null); })
        .catch(() => {});
    };
    window.addEventListener("nutramilo:ai-report-generated", refresh);
    return () => {
      cancelled = true;
      window.removeEventListener("nutramilo:ai-report-generated", refresh);
    };
  }, [API]);

  // Nothing to show yet
  if (loading || !reportDoc || !reportDoc.report) return null;

  const teaser = extractTeaser(reportDoc.report);
  const m = reportDoc.standard_metrics || {};
  const generatedAt = reportDoc.generated_at ? new Date(reportDoc.generated_at) : null;
  const dateStr = generatedAt && !isNaN(generatedAt.getTime())
    ? generatedAt.toLocaleDateString(bg ? "bg-BG" : "en-US", { day: "numeric", month: "long", year: "numeric" })
    : "";

  const cardBase = isDark
    ? "bg-gradient-to-br from-violet-900/25 via-[hsl(222,47%,10%)] to-fuchsia-900/15 border-violet-500/25"
    : "bg-gradient-to-br from-violet-50 via-white to-fuchsia-50 border-violet-200";

  return (
    <>
      <section
        className={`rounded-2xl border p-5 transition-all hover:shadow-md ${cardBase}`}
        data-testid="cc-last-ai-report"
      >
        <div className="flex items-start gap-2 mb-3">
          <Sparkles className={`w-4 h-4 mt-0.5 shrink-0 ${isDark ? "text-violet-300" : "text-violet-600"}`} />
          <div className="min-w-0 flex-1">
            <div className={`text-[10px] uppercase tracking-wider font-bold ${isDark ? "text-violet-300" : "text-violet-700"}`}>
              {bg ? "Последен AI доклад" : "Latest AI report"}
            </div>
            {dateStr && (
              <div className={`text-[11px] ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                {dateStr}
              </div>
            )}
          </div>
        </div>

        {teaser && (
          <p
            className={`text-xs leading-relaxed mb-4 line-clamp-4 ${isDark ? "text-gray-300" : "text-gray-700"}`}
            data-testid="cc-last-ai-report-teaser"
          >
            {teaser}
          </p>
        )}

        <div className="grid grid-cols-4 gap-2 mb-4">
          <MetricPill icon={Flame}   label={bg ? "BMR" : "BMR"}     value={m.bmr}      unit="kcal" isDark={isDark} />
          <MetricPill icon={Target}  label={bg ? "TDEE" : "TDEE"}   value={m.tdee}     unit="kcal" isDark={isDark} />
          <MetricPill icon={Utensils} label={bg ? "Протеин" : "Protein"} value={m.protein} unit="g"    isDark={isDark} />
          <MetricPill icon={Utensils} label={bg ? "Въгл." : "Carbs"} value={m.carbs}   unit="g"    isDark={isDark} />
        </div>

        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => setShowFull(true)}
            data-testid="cc-last-ai-report-view-full"
            className={`flex-1 inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-full text-sm font-semibold transition-colors ${
              isDark ? "bg-violet-500/20 text-violet-100 hover:bg-violet-500/30" : "bg-violet-600 text-white hover:bg-violet-700"
            }`}
          >
            <FileText className="w-4 h-4" />
            {bg ? "Виж пълния анализ" : "View full analysis"}
          </button>
          <button
            type="button"
            onClick={regenerate}
            disabled={regenerating}
            data-testid="cc-last-ai-report-regenerate"
            aria-label={bg ? "Обнови доклада" : "Refresh report"}
            title={bg ? "Обнови с текущи данни от досието" : "Refresh with latest health record data"}
            className={`inline-flex items-center justify-center px-3 py-2.5 rounded-full text-sm font-semibold transition-colors disabled:opacity-60 ${
              isDark ? "bg-white/10 text-violet-100 hover:bg-white/20" : "bg-white text-violet-700 border border-violet-200 hover:bg-violet-50"
            }`}
          >
            <RefreshCw className={`w-4 h-4 ${regenerating ? "animate-spin" : ""}`} />
          </button>
        </div>
      </section>

      {/* Full report modal */}
      {showFull && (
        <div
          className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm"
          onClick={() => setShowFull(false)}
          data-testid="cc-last-ai-report-modal"
        >
          <div
            className={`w-full sm:max-w-2xl rounded-t-3xl sm:rounded-2xl p-6 max-h-[90vh] overflow-y-auto shadow-2xl ${
              isDark ? "bg-[hsl(222,47%,12%)] text-white" : "bg-white text-[#1A1A2E]"
            }`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-violet-500" />
                <h3 className="text-lg font-bold">
                  {bg ? "Персонализиран AI здравен доклад" : "Personalized AI Health Report"}
                </h3>
              </div>
              <button
                type="button"
                onClick={() => setShowFull(false)}
                data-testid="cc-last-ai-report-modal-close"
                aria-label={bg ? "Затвори" : "Close"}
                className={`p-1 rounded-full ${isDark ? "hover:bg-white/10" : "hover:bg-gray-100"}`}
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            {dateStr && (
              <p className={`text-xs mb-4 ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                {bg ? "Генериран на" : "Generated"} {dateStr}
                {reportDoc.goal && ` · ${bg ? "цел" : "goal"}: ${reportDoc.goal}`}
              </p>
            )}
            <div
              className={`text-sm leading-relaxed whitespace-pre-line ${isDark ? "text-gray-300" : "text-gray-700"}`}
              data-testid="cc-last-ai-report-modal-body"
            >
              {reportDoc.report}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function MetricPill({ icon: Icon, label, value, unit, isDark }) {
  const hasValue = value != null && !Number.isNaN(Number(value));
  return (
    <div className={`rounded-xl p-2 border ${isDark ? "bg-white/[0.03] border-white/5" : "bg-white/70 border-gray-200"}`}>
      <div className="flex items-center gap-1 mb-0.5">
        <Icon className={`w-3 h-3 ${isDark ? "text-violet-300" : "text-violet-600"}`} />
        <span className={`text-[9px] uppercase tracking-wider font-semibold ${isDark ? "text-gray-400" : "text-gray-500"}`}>
          {label}
        </span>
      </div>
      <div className={`text-sm font-bold tabular-nums ${isDark ? "text-white" : "text-[#1A1A2E]"}`}>
        {hasValue ? (
          <>
            {Math.round(Number(value))}
            <span className={`text-[9px] font-normal ml-0.5 ${isDark ? "text-gray-400" : "text-gray-500"}`}>{unit}</span>
          </>
        ) : (
          <span className={`text-xs font-normal ${isDark ? "text-gray-500" : "text-gray-400"}`}>—</span>
        )}
      </div>
    </div>
  );
}
